import React, { useState, useEffect } from "react";
import Iconify from "../../components/Iconify";
import { Button, Avatar, Tooltip } from "@mui/material";
import CustomTable from "../../components/customTable/CustomTable";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../../Hooks/AdminContext";
import StatusChip from "../../theme/chip";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import { _Delete_Customer, _User_List } from "../../DAL/Listing/UserListing";
import CustomCircularProgress from "../../components/CustomCircularProgress";
import LockResetIcon from "@mui/icons-material/LockReset";
import ChangePasswordAdmin from "./ChangePasswordAdmin";
import CustomModal from "../../components/CustomModel";
import { picBaseUrl } from "../../config/config";
import { show_proper_words } from "../../utils/constant";

const UserManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const { setnavbarTitle } = useAdminContext();
  const { enqueueSnackbar } = useSnackbar();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [rowDataID, setRowDataID] = useState("");
  const [rowData, setRowData] = useState({});
  const [openDelete, setOpenDelete] = useState(false);
  const [userList, setUserList] = useState([]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(userList.length);
  const [pageCount, setPageCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const handleShowChangePassword = () => {
    setShowChangePassword(true);
  };

  const handleOpenModal = () => {
    navigate(`/user-management/add-user`);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    const response = await _Delete_Customer(rowDataID);
    if (response.code === 200) {
      setUserList((prevUserList) => {
        const updatedUserList = prevUserList.filter(
          (user) => user.user_id._id !== rowDataID
        );
        return updatedUserList;
      });
      enqueueSnackbar("User deleted successfully", { variant: "success" });
      setTotalCount((prev) => prev - 1);
      setOpenDelete(false);
    } else {
      enqueueSnackbar("Failed to delete user", { variant: "error" });
    }
    setIsLoading(false);
  };

  const handleAgreeDelete = (row) => {
    console.log(rowData);
    setRowDataID(row.user_id._id);
    setRowData(row);
    setOpenDelete(true);
  };

  const searchFunction = (e) => {
    e.preventDefault();
    setSearchText(searchText);
    localStorage.setItem("searchText_User_Managements", searchText);
    setPage(0);
    setTotalPages(0);
    GetUserList(0, rowsPerPage, searchText);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  const User_Table_Head = [
    {
      id: "action",
      label: "Action",
      renderData: (row) => (
        <>
          <Tooltip title="Edit User" placement="top">
            <EditIcon
              onClick={() => {
                navigate(`/user-management/edit-user/${row.user_id._id}`, {
                  state: row,
                });
              }}
              style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title="Delete User" placement="top">
            <DeleteOutlineIcon
              onClick={() => {
                handleAgreeDelete(row);
              }}
              style={{
                fill: "#ff0000",
                marginRight: "10px",
                cursor: "pointer",
              }}
            />
          </Tooltip>
          <Tooltip title="View Profile" placement="top">
            <Person2OutlinedIcon
              onClick={() => {
                navigate(`/user-management/user-profile/${row.user_id._id}`);
              }}
              style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title="Change Password" placement="top">
            <LockResetIcon
              onClick={() => {
                setRowDataID(row.user_id._id);
                handleShowChangePassword();
              }}
              style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
            />
          </Tooltip>
        </>
      ),
    },
    {
      id: "number",
      label: "#",
      type: "number",
    },
    {
      id: "avatar",
      label: "Image",
      renderData: (row) => (
        <Avatar
          alt={row.name}
          src={picBaseUrl + row.profile_image || ""}
          sx={{ width: 40, height: 40, transform: "rotate(360deg)" }}
        />
      ),
    },
    {
      id: "email",
      label: "User Info",
      renderData: (row) => {
        return (
          <span>
            <div>
              {show_proper_words(
                row?.first_name + " " + row?.last_name || "_ _"
              ) || "_ _"}
            </div>
            <div className="text-muted">{row?.user_id?.email || "_ _"}</div>
          </span>
        );
      },
    },
    {
      id: "",
      label: "Refer By",
      renderData: (row) => {
        return (
          <span>
            {row?.refer_by ? (
              <>
                <div className=" capitalized">
                  {show_proper_words(
                    row?.refer_by?.first_name + " " + row?.refer_by?.last_name
                  ) || "_ _"}
                </div>
                <div className="text-muted">
                  {row?.refer_by?.email || "_ _"}
                </div>
              </>
            ) : (
              "_ _"
            )}
          </span>
        );
      },
    },
    {
      id: "phone",
      label: "Phone Number",
      renderData: (row) => <span>{row?.contact_number || "_ _"}</span>,
    },
    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <StatusChip status={row?.status} type="activeInactive" />
      ),
    },
  ];

  const GetUserList = async (page, rowsPerPage, searchText) => {
    setLoading(true);
    const FormData = {
      search: searchText,
    };
    const response = await _User_List(FormData, page, rowsPerPage);
    if (response.code === 200) {
      const { customer_list, total_pages, total_count } = response;
      setUserList(customer_list);
      setTotalCount(total_count);
      setTotalPages(total_pages);
      setLoading(false);
    } else {
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  useEffect(() => {
    const searcthecxt = localStorage.getItem("searchText_User_Managements");
    setnavbarTitle("User Management");
    if (searcthecxt) {
      GetUserList(page, rowsPerPage, searcthecxt);
    } else {
      GetUserList(page, rowsPerPage, searchText);
    }
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  if (loading) {
    return <CustomCircularProgress />;
  }
  return (
    <div className="mt-4">
      <CustomModal
        crossButton={true}
        CustomSpacing={true}
        open={showChangePassword}
        handleClose={() => setShowChangePassword(false)}
        component={
          <ChangePasswordAdmin
            rowDataID={rowDataID}
            handleClose={() => setShowChangePassword(false)}
          />
        }
      />
      <DeleteConfirmation
        isLoading={isLoading}
        open={openDelete}
        setRowData={setRowData}
        setRowDataID={setRowDataID}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this user?"}
        handleAgree={handleDelete}
        handleClose={() => setOpenDelete(false)}
      />

      {/* Add New User and Investigator Buttons */}
      <div className="d-flex justify-content-end mb-3 add-button">
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={handleOpenModal}
        >
          Create New User
        </Button>
      </div>

      {/* User List Table */}
      <div className="mt-3">
        <CustomTable
          data={userList}
          TABLE_HEAD={User_Table_Head}
          MENU_OPTIONS={[]}
          custom_search={{
            searchText: searchText,
            setSearchText: setSearchText,
            handleSubmit: searchFunction,
          }}
          custom_pagination={{
            total_count: totalCount,
            rows_per_page: rowsPerPage,
            page: page,
            handleChangePage: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
          pageCount={pageCount}
          totalPages={totalPages}
          pagePagination={true}
          handleChangePages={handleChangePages}
          placeHoldertext="Search by name or email"
        />
      </div>
    </div>
  );
};

export default UserManagement;
